import { create } from 'zustand';

const useLayoutsStore = create((set) => ({
  // State
  mediaLayouts: [],
  widgetLayouts: [],
  isLoading: false,
  error: null,

  // Actions
  fetchMediaLayouts: async (accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/api/layouts/media', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch media layouts');
      }

      set({ mediaLayouts: data.layouts, isLoading: false });
      return { success: true };
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return { success: false, error: error.message };
    }
  },

  fetchWidgetLayouts: async (accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/api/layouts/widget', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch widget layouts');
      }

      set({ widgetLayouts: data.layouts, isLoading: false });
      return { success: true };
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return { success: false, error: error.message };
    }
  },

  updateMediaLayout: async (layoutId, updateData, accessToken) => {
    try {
      const response = await fetch(`/api/layouts/media/${layoutId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update layout');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  updateWidgetLayout: async (layoutId, updateData, accessToken) => {
    try {
      const response = await fetch(`/api/layouts/widget/${layoutId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update layout');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  reorderMediaLayouts: async (layoutOrder, accessToken) => {
    try {
      const response = await fetch('/api/layouts/media/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ layoutOrder }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reorder layouts');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  reorderWidgetLayouts: async (layoutOrder, accessToken) => {
    try {
      const response = await fetch('/api/layouts/widget/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ layoutOrder }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reorder layouts');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  uploadMediaLayoutPreview: async (layoutId, file, accessToken) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/layouts/media/${layoutId}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload preview');
      }

      return { success: true, preview_uuid: data.preview_uuid };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  uploadWidgetLayoutPreview: async (layoutId, file, accessToken) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/layouts/widget/${layoutId}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload preview');
      }

      return { success: true, preview_uuid: data.preview_uuid };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  clearError: () => set({ error: null }),
}));

export { useLayoutsStore };
